import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Still Field Mode's: not yet sent in full. A batch the outbox has CREATED on
 * the server (serverSampleId) but whose photos and submit are still to go
 * counts too: the server's copy of it is an unfinished draft until then, so a
 * screen shows the device's version and hides the server row with that id
 * (serverIdsOf). Counting it as arrived at creation re-read the server at the
 * one moment its copy was a photo-less draft, and never again.
 */
export const unsent = batch => batch.status !== 'sent';

/** The server ids of these batches' records, created but not yet complete. */
export const serverIdsOf = batches => new Set(batches.map(b => b.serverSampleId).filter(Boolean));

/**
 * This person's Field Mode and map records still on the device, newest
 * first, kept current as the store changes -- the map's grey pins and the
 * Collections page's "On this device" rows, from one reading.
 *
 * When one leaves this list it has been sent in full (or removed), so
 * `onFewer` is called (each screen re-reads the server). It is read through a
 * ref: a new function on every render does not subscribe again.
 *
 * The camera's unfinished batch (`active`) is in the list; a screen that does
 * not show it leaves it out itself.
 */
export default function useUnsentBatches(store, userId, onFewer) {
  const [batches, setBatches] = useState([]);
  const fewer = useRef(onFewer);
  fewer.current = onFewer;
  const count = useRef(0);

  const read = useCallback(async () => {
    if (!userId) return [];
    try {
      const list = (await store.list(userId)).filter(unsent);
      if (list.length < count.current) fewer.current?.();
      count.current = list.length;
      return list;
    } catch {
      return [];
    }
  }, [store, userId]);

  const shown = useRef(batches);
  useEffect(() => {
    let alive = true;
    // Nothing on the device, as before, is no change (most people's): the
    // screen is not drawn again for it.
    const refresh = () => read().then((list) => {
      if (!alive || (list.length === 0 && shown.current.length === 0)) return;
      shown.current = list;
      setBatches(list);
    });
    refresh();
    const off = store.subscribe(refresh);
    return () => { alive = false; off(); };
  }, [store, read]);

  return batches;
}
